import { cn } from "@workspace/ui/lib/utils";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { usePhoneSize } from "../hooks/usePhoneSize";
import { useClientStore } from "../store";
import { Hero } from "./Hero";
import { IMessageScreen } from "./IMessageScreen";
import { IPhoneMockup } from "./ui/phones/iphone";

const LANDING_EASE = [0.22, 1, 0.36, 1] as const;

/** Glass container opacity (0-100). Adjust to control translucency. */
const GLASS_OPACITY = {
  landing: 50,
  default: 22,
};

/**
 * Mobile hero horizontal padding in pixels.
 * Adjust this value to control edge spacing on mobile.
 */
const MOBILE_HERO_PADDING_PX = 16;

export function LandingPage() {
  const phoneSize = usePhoneSize();
  const landingVideoComplete = useClientStore((s) => s.landingVideoComplete);

  // Direct mobile check - works on initial render (store's isMobile may be stale)
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < 768;
  });

  // Keep in sync with window resizes
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [hasSeenTypewriter, setHasSeenTypewriter] = useState(() => {
    return localStorage.getItem("has-seen-typewriter") === "true";
  });

  const onTypingComplete = () => {
    localStorage.setItem("has-seen-typewriter", "true");
    setHasSeenTypewriter(true);
  };

  // Constrain max-width to create visual padding on mobile
  const heroMaxWidth = isMobile
    ? `calc(100vw - ${MOBILE_HERO_PADDING_PX * 2}px)`
    : undefined;

  const scaleStyles: React.CSSProperties = {
    maxWidth: heroMaxWidth,
  };

  return (
    <>
      <style>{`
        @media (width >= 90rem) {
          .home-hero-container {
            max-width: 72rem;
          }
        }
      `}</style>
      {/* Outer wrapper - centers content */}
      <div className="flex h-full w-full pt-12 md:pt-0 items-center justify-center">
        {/* Inner container for desktop layout */}
        <div
          className={cn(
            "home-hero-container flex w-full items-center justify-center md:max-w-4xl lg:max-w-4xl lg:justify-between xl:max-w-5xl xl:gap-16"
          )}
        >
          <motion.div
            animate={
              landingVideoComplete
                ? { opacity: 1, x: 0 }
                : { opacity: 0, x: -40 }
            }
            className="origin-center"
            initial={{ opacity: 0, x: -40 }}
            style={scaleStyles}
            transition={{ duration: 0.5, ease: LANDING_EASE }}
          >
            <Hero
              glassOpacity={GLASS_OPACITY.landing}
              isLandingPage
              onTypingComplete={onTypingComplete}
              skipAnimation={hasSeenTypewriter}
            />
          </motion.div>

          <div className={cn("hidden lg:block")}>
            <motion.div
              animate={
                landingVideoComplete
                  ? { opacity: 1, x: 0 }
                  : { opacity: 0, x: 40 }
              }
              initial={{ opacity: 0, x: 40 }}
              transition={{ duration: 0.5, ease: LANDING_EASE, delay: 0.08 }}
            >
              <motion.div
                animate={
                  landingVideoComplete
                    ? {
                        y: [0, -8, -14, -10, -16, -6, 0],
                        x: [0, 2, -1, 3, -2, 1, 0],
                        rotate: [0, 0.5, -0.3, 0.4, -0.5, 0.2, 0],
                      }
                    : {}
                }
                className="levitate-shadow rounded-[2.5rem]"
                transition={{
                  duration: 10,
                  ease: "easeInOut",
                  repeat: Number.POSITIVE_INFINITY,
                  times: [0, 0.15, 0.35, 0.5, 0.7, 0.85, 1],
                }}
              >
                <IPhoneMockup showShadow={false} size={phoneSize}>
                  <IMessageScreen size={phoneSize} />
                </IPhoneMockup>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </>
  );
}
